// src/pages/VolpeTrackAccountDeletion.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiGlobe, FiChevronLeft, FiTrash2, FiMail, FiClock, FiAlertTriangle } from 'react-icons/fi';
import SEO from '../components/SEO';

// Styled components para la página de eliminación de cuenta
const PageContainer = styled.div`
  padding-top: 80px; /* Para la navbar fija */
  min-height: 100vh;
  background: linear-gradient(135deg, #1A365D 0%, #0B1D3A 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.4;
    z-index: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  width: 100%;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: var(--spacing-xl) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.5;
    z-index: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-md);
  color: var(--white);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-md);
  
  svg {
    font-size: 3rem;
    color: var(--white);
    opacity: 0.9;
  }
`;

const LastUpdated = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const DeletionContainer = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);
  
  @media (max-width: 768px) {
    padding: var(--spacing-lg);
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-top: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  color: var(--secondary);
  border-bottom: 2px solid var(--accent);
  padding-bottom: 0.5rem;
  display: flex;
  align-items: center;
  
  &:first-of-type {
    margin-top: 0;
  }
  
  svg {
    margin-right: var(--spacing-sm);
    color: var(--accent);
  }
`;

const DeletionText = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-md);
`;

const List = styled.ul`
  margin-bottom: var(--spacing-md);
  padding-left: var(--spacing-lg);
`;

const ListItem = styled.li`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-xs);
  position: relative;
  
  &::marker {
    color: var(--accent);
  }
`;

const ContactBox = styled.div`
  background-color: rgba(71, 181, 255, 0.1);
  border: 1px solid var(--accent);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin: var(--spacing-md) 0;
`;

const WarningBox = styled.div`
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #FF6B6B;
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin: var(--spacing-md) 0;
  display: flex;
  align-items: flex-start;
  
  svg {
    color: #FF6B6B;
    font-size: 1.2rem;
    margin-right: var(--spacing-sm);
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  cursor: pointer;
  background: none;
  border: none;
  text-decoration: none;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(8px);
  transition: all var(--transition-fast);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;
  
  svg {
    margin-right: var(--spacing-xs);
  }
  
  &:hover {
    color: var(--white);
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const LanguageToggle = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 0.75rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 5;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
  }
`;

const LanguageIcon = styled.div`
  margin-right: var(--spacing-xs);
  display: flex;
  align-items: center;
  color: var(--white);
`;

const LanguageText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--white);
`;

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const VolpeTrackAccountDeletion = () => {
  // Fecha de última actualización
  const lastUpdated = "Oct 22, 2025"; 
  
  // Idioma (EN por defecto para producción)
  const [language, setLanguage] = useState('en'); // 'en' | 'es'

  const toggleLanguage = () => setLanguage(language === 'en' ? 'es' : 'en');
  const handleBackClick = () => { window.location.href = '/'; };
  useEffect(() => { window.scrollTo(0, 0); }, []);
  
  return (
    <>
      <SEO routeKey="volpeTrackAccountDeletion" />
      <PageContainer>
        <ContentWrapper>
        <TopBar>
          <BackButton onClick={handleBackClick}>
            <FiChevronLeft /> {language === 'en' ? 'Back to Home' : 'Volver al inicio'}
          </BackButton>
          <LanguageToggle onClick={toggleLanguage}>
            <LanguageIcon><FiGlobe /></LanguageIcon>
            <LanguageText>{language === 'en' ? 'Español' : 'English'}</LanguageText>
          </LanguageToggle>
        </TopBar>
        
        <PageHeader>
          <HeaderIcon>
            <FiTrash2 />
          </HeaderIcon>
          <PageTitle>
            {language === 'en' ? 'VolpeTrack Account Deletion' : 'Eliminación de Cuenta VolpeTrack'}
          </PageTitle>
          <LastUpdated>
            {language === 'en' ? `Last updated: ${lastUpdated}` : `Última actualización: ${lastUpdated}`}
          </LastUpdated>
        </PageHeader>
        
        {language === 'en' ? (
          <DeletionContainer initial="hidden" animate="visible" variants={fadeIn}>
            <DeletionText>
              <strong>Provider:</strong> XADevs LLC<br />
              We respect your privacy and your right to control your data. Below we explain how to delete your VolpeTrack account and what happens to your information.
            </DeletionText>

            <SectionTitle><FiTrash2 /> How to delete your account (in-app)</SectionTitle>
            <DeletionText>You can request permanent deletion directly in the app:</DeletionText>
            <List>
              <ListItem>Open VolpeTrack</ListItem>
              <ListItem>Go to <strong>Settings → Delete account</strong></ListItem>
              <ListItem>Review the summary and tap <strong>Confirm deletion</strong></ListItem>
            </List>
            <DeletionText>
              The request is immediate and your active data is queued for secure deletion. You will see a confirmation in the app and, if applicable, by email.
            </DeletionText>

            <ContactBox>
              <DeletionText style={{ margin: 0 }}>
                <strong>📧 Prefer email?</strong> You can also write from the email linked to your account to <strong>privacy@xadevs.com</strong> with subject: <em>"VolpeTrack Account Deletion"</em>. We will verify ownership and process the same flow.
              </DeletionText>
            </ContactBox>

            <SectionTitle>What data is deleted</SectionTitle>
            <DeletionText>Upon confirmation, we permanently delete:</DeletionText>
            <List>
              <ListItem>Your profile (name, photo) and the account email (used as identifier)</ListItem>
              <ListItem>All your financial records (income, expenses, categories, goals, notes)</ListItem>
              <ListItem>Preferences and settings (language, currency, theme, notifications, achievements)</ListItem>
              <ListItem>Interactions with the AI assistant and related metadata</ListItem>
              <ListItem>Gamification data and badges associated with your account</ListItem>
            </List>

            <WarningBox>
              <FiAlertTriangle />
              <DeletionText style={{ margin: 0 }}>
                Deletion is <strong>irreversible</strong>. Once completed, it is not possible to recover the account or data.
              </DeletionText>
            </WarningBox>

            <SectionTitle>What may be retained temporarily</SectionTitle>
            <DeletionText>For security, reliability, and compliance, we retain:</DeletionText>
            <List>
              <ListItem>Encrypted backups and logs for a maximum of <strong>60 days</strong>, then automatically purged</ListItem>
              <ListItem>Billing records that Google Play / App Store maintain according to their policies</ListItem>
              <ListItem>Minimal records necessary to address prior support or legal obligations</ListItem>
            </List>
            <DeletionText>
              These retained elements are not used to operate your account and are destroyed after the retention period unless law requires otherwise.
            </DeletionText>

            <SectionTitle>Subscriptions and purchases</SectionTitle>
            <DeletionText>
              If you have an active <strong>Premium Pro</strong> subscription, deleting the account does <strong>not</strong> cancel store billing. Cancel in Google Play / App Store to avoid future charges.
            </DeletionText>
            <DeletionText>
              <strong>Founder Pioneer</strong> is lifetime access. After deleting the account, that access is disabled for the deleted account.
            </DeletionText>

            <SectionTitle>Ads and plan status</SectionTitle>
            <DeletionText>
              <strong>Free (Ad-Supported)</strong> deleted accounts immediately stop making ad requests.
            </DeletionText>
            <DeletionText>
              <strong>Premium Pro / Founder Pioneer</strong> accounts are also deleted entirely under the above rules.
            </DeletionText>

            <SectionTitle><FiClock /> Verification and timeframes</SectionTitle>
            <DeletionText>In-app deletion requires you to be logged in; by email, we will verify ownership.</DeletionText>
            <DeletionText>Active data deletion begins immediately. Backup/log purging completes within <strong>60 days</strong>.</DeletionText>

            <SectionTitle><FiMail /> Questions</SectionTitle>
            <ContactBox>
              <DeletionText style={{ margin: 0 }}>
                Privacy: <strong>privacy@xadevs.com</strong><br />
                Support: <strong>support@xadevs.com</strong><br />
                See our <strong>Privacy Policy</strong> and <strong>Terms</strong> for complete information.
              </DeletionText>
            </ContactBox>
          </DeletionContainer>
        ) : (
          <DeletionContainer initial="hidden" animate="visible" variants={fadeIn}>
            <DeletionText>
              <strong>Titular:</strong> XADevs LLC<br />
              Respetamos tu privacidad y tu derecho a controlar tus datos. A continuación explicamos cómo eliminar tu cuenta de VolpeTrack y qué ocurre con tu información.
            </DeletionText>

            <SectionTitle><FiTrash2 /> Cómo eliminar tu cuenta (in-app)</SectionTitle>
            <DeletionText>Puedes solicitar la eliminación permanente directamente en la app:</DeletionText>
            <List>
              <ListItem>Abre VolpeTrack</ListItem>
              <ListItem>Ve a <strong>Ajustes → Eliminar cuenta</strong></ListItem>
              <ListItem>Revisa el resumen y pulsa <strong>Confirmar eliminación</strong></ListItem>
            </List>
            <DeletionText>
              La solicitud es inmediata y tus datos activos se ponen en cola para borrado seguro. Verás una confirmación en la app y, si aplica, por email.
            </DeletionText>

            <ContactBox>
              <DeletionText style={{ margin: 0 }}>
                <strong>📧 ¿Prefieres email?</strong> También puedes escribir desde el correo vinculado a <strong>privacy@xadevs.com</strong> con asunto: <em>"Eliminación de cuenta VolpeTrack"</em>. Verificaremos la titularidad y procesaremos el mismo flujo.
              </DeletionText>
            </ContactBox>

            <SectionTitle>Qué datos se eliminan</SectionTitle>
            <DeletionText>Con la confirmación, borramos de forma permanente:</DeletionText>
            <List>
              <ListItem>Tu perfil (nombre, foto) y el correo de la cuenta (usado como identificador)</ListItem>
              <ListItem>Todos tus registros financieros (ingresos, gastos, categorías, metas, notas)</ListItem>
              <ListItem>Preferencias y ajustes (idioma, moneda, tema, notificaciones, logros)</ListItem>
              <ListItem>Interacciones con el asistente de IA y metadatos relacionados</ListItem>
              <ListItem>Datos de gamificación y medallas asociados a tu cuenta</ListItem>
            </List>

            <WarningBox>
              <FiAlertTriangle />
              <DeletionText style={{ margin: 0 }}>
                La eliminación es <strong>irreversible</strong>. Una vez completada, no es posible recuperar la cuenta ni los datos.
              </DeletionText>
            </WarningBox>

            <SectionTitle>Qué puede conservarse temporalmente</SectionTitle>
            <DeletionText>Por seguridad, fiabilidad y cumplimiento conservamos:</DeletionText>
            <List>
              <ListItem>Copias de seguridad y logs cifrados durante un máximo de <strong>60 días</strong>, tras lo cual se purgan automáticamente</ListItem>
              <ListItem>Registros de facturación que Google Play / App Store mantienen según sus políticas</ListItem>
              <ListItem>Registros mínimos necesarios para atender soporte previo u obligaciones legales</ListItem>
            </List>
            <DeletionText>
              Estos elementos retenidos no se usan para operar tu cuenta y se destruyen tras el plazo salvo exigencia legal.
            </DeletionText>

            <SectionTitle>Suscripciones y compras</SectionTitle>
            <DeletionText>
              Si tienes una suscripción <strong>Premium Pro</strong> activa, eliminar la cuenta <strong>no</strong> cancela la facturación de la tienda. Cancela en Google Play / App Store para evitar cargos futuros.
            </DeletionText>
            <DeletionText>
              <strong>Founder Pioneer</strong> es un acceso de por vida. Tras eliminar la cuenta, ese acceso queda inhabilitado para la cuenta eliminada.
            </DeletionText>

            <SectionTitle>Anuncios y estado del plan</SectionTitle>
            <DeletionText>
              Las cuentas <strong>Gratuito (Ad-Supported)</strong> eliminadas dejan de realizar solicitudes de anuncios de inmediato.
            </DeletionText>
            <DeletionText>
              Las cuentas <strong>Premium Pro / Founder Pioneer</strong> también se eliminan íntegramente con las reglas anteriores.
            </DeletionText>

            <SectionTitle><FiClock /> Verificación y plazos</SectionTitle>
            <DeletionText>La eliminación en la app requiere que tengas sesión iniciada; por email, verificaremos la titularidad.</DeletionText>
            <DeletionText>El borrado de datos activos comienza de inmediato. La purga de copias de seguridad/logs finaliza en <strong>60 días</strong>.</DeletionText>

            <SectionTitle><FiMail /> Dudas</SectionTitle>
            <ContactBox>
              <DeletionText style={{ margin: 0 }}>
                Privacidad: <strong>privacy@xadevs.com</strong><br />
                Soporte: <strong>support@xadevs.com</strong><br />
                Consulta nuestra <strong>Política de Privacidad</strong> y <strong>Términos</strong> para información completa.
              </DeletionText>
            </ContactBox>
          </DeletionContainer>
          )}
        </ContentWrapper>
      </PageContainer>
    </>
  );
};

export default VolpeTrackAccountDeletion;
